/* eslint-disable react-hooks/exhaustive-deps */
import { useEffect, useState } from "react";
import { Box, Tab, CircularProgress, Typography } from "@mui/material";
import { TabContext, TabList, TabPanel } from "@mui/lab";
import { tabbedDataGridPage } from "../../styles/commonPageStyles";
import Clusters from "./Clusters";
import { useNavigate, useParams } from "react-router-dom";
import { useListDeploymentStages } from "../../api/deploymentStages";
import { styles } from "./styles";

// Used when the deployment-stages API isn't available (e.g. 404), so the
// page still has something to show instead of "No deployment stages".
const FALLBACK_DEPLOYMENT_STAGES = ["dev", "qa", "stage", "prod"];

// The tab chain is stretched to the viewport so the grid inside a panel can
// claim whatever height is left below the tab list, matching the Tessell GCP
// control planes page.
const fillHeight = {
  mainBox: {
    ...tabbedDataGridPage.mainBox,
    height: "100vh",
    display: "flex",
    flexDirection: "column",
  },
  tabContainer: {
    ...tabbedDataGridPage.tabContainer,
    flex: 1,
    minHeight: 0,
    display: "flex",
    flexDirection: "column",
  },
  tabPanel: {
    flex: 1,
    minHeight: 0,
    display: "flex",
    flexDirection: "column",
    paddingLeft: "4px",
    // `display: flex` above would override the browser's [hidden] rule and
    // make the inactive panel claim flex space — keep it collapsed.
    "&[hidden]": { display: "none" },
  },
};

const ClustersPage = () => {
  const [selectedTab, setSelectedTab] = useState("dev");
  const [tabs, setTabs] = useState([]);
  const navigate = useNavigate();
  const { deploymentStage } = useParams();

  const {
    postData: listDeploymentStages,
    response: deploymentStagesResp,
    responseStatus: deploymentStagesStatus,
    error: deploymentStagesError,
    isLoading: isLoadingDeploymentStages,
  } = useListDeploymentStages();

  useEffect(() => {
    listDeploymentStages();
  }, []);

  useEffect(() => {
    const stageNames = deploymentStagesResp?.deploymentStages?.map(
      (stage) => stage.name
    );
    const failed =
      deploymentStagesError ||
      (typeof deploymentStagesStatus === "number" &&
        deploymentStagesStatus !== 200);
    const resolvedStages = stageNames?.length
      ? stageNames
      : failed
      ? FALLBACK_DEPLOYMENT_STAGES
      : null;

    if (resolvedStages) {
      setTabs(resolvedStages);

      if (deploymentStage && resolvedStages.includes(deploymentStage)) {
        setSelectedTab(deploymentStage);
      } else if (resolvedStages.length > 0) {
        setSelectedTab(resolvedStages[0]);
        navigate(
          `/apps/tessell/deployment-stages/${resolvedStages[0]}/k8s-clusters`
        );
      }
    }
  }, [deploymentStagesResp, deploymentStagesStatus, deploymentStagesError]);

  useEffect(() => {
    if (deploymentStage && tabs.includes(deploymentStage)) {
      setSelectedTab(deploymentStage);
    }
  }, [deploymentStage, tabs]);

  if (isLoadingDeploymentStages) {
    return (
      <Box sx={styles.loadingBox}>
        <CircularProgress color="secondary" />
      </Box>
    );
  }

  if (tabs.length === 0) {
    return (
      <Box sx={styles.loadingBox}>
        <Typography variant="h5">No deployment stages available</Typography>
      </Box>
    );
  }

  return (
    <Box sx={fillHeight.mainBox}>
      <Box sx={fillHeight.tabContainer}>
        <TabContext value={selectedTab}>
          <Box sx={tabbedDataGridPage.tabListBox}>
            <TabList
              onChange={(_e, newValue) => {
                navigate(
                  `/apps/tessell/deployment-stages/${newValue}/k8s-clusters`
                );
              }}
              sx={tabbedDataGridPage.tabList}
            >
              {tabs.map((tab, i) => (
                <Tab
                  key={i}
                  label={tab}
                  value={tab}
                  icon={<></>}
                  iconPosition="end"
                ></Tab>
              ))}
            </TabList>
          </Box>
          {tabs.map((tab, i) => (
            <TabPanel key={i} value={tab} sx={fillHeight.tabPanel}>
              <Clusters />
            </TabPanel>
          ))}
        </TabContext>
      </Box>
    </Box>
  );
};

export default ClustersPage;
